import { data, Link, useSearchParams } from "react-router";
import { BlogCard } from "#app/components/blog/blog-card.tsx";
import { Pagination } from "#app/components/navigation/Pagination.tsx";
import { Icon } from "#app/components/ui/icon.tsx";
import {
	CATEGORY_LABELS,
	type Category,
} from "#app/utils/blog-types.ts";
import {
	getAllTags,
	getBlogPostList,
} from "#app/utils/blog.server.ts";
import { type Route } from "./+types/_index.ts";

export async function loader({ request }: Route.LoaderArgs) {
	const url = new URL(request.url);
	const page = Math.max(1, Number(url.searchParams.get("page")) || 1);
	const category = url.searchParams.get("category") || undefined;
	const tag = url.searchParams.get("tag") || undefined;
	const limit = 12;

	const [{ posts, totalCount }, tags] = await Promise.all([
		getBlogPostList({ category, tag, page, limit }),
		getAllTags(),
	]);

	return data({
		posts,
		tags,
		pagination: {
			currentPage: page,
			totalPages: Math.ceil(totalCount / limit),
			totalCount,
			limit,
		},
		filters: { category, tag },
	});
}

export const meta: Route.MetaFunction = () => [
	{ title: "Blog - shonaslang" },
	{
		name: "description",
		content:
			"Deep dives into Shona slang origins, cultural roundups, and stories from the Zimbabwean community.",
	},
	{ property: "og:title", content: "shonaslang Blog" },
	{
		property: "og:description",
		content:
			"Deep dives into Shona slang origins, cultural roundups, and stories from the Zimbabwean community.",
	},
	{
		tagName: "link",
		rel: "alternate",
		type: "application/rss+xml",
		title: "shonaslang Blog RSS",
		href: "/blog/rss.xml",
	},
];

const categories = Object.entries(CATEGORY_LABELS) as [Category, string][];

export default function BlogIndex({ loaderData }: Route.ComponentProps) {
	const { posts, tags, pagination, filters } = loaderData;
	const [searchParams] = useSearchParams();

	return (
		<div className="container mx-auto max-w-6xl px-4 py-8">
			{/* Header */}
			<div className="mb-8">
				<div className="flex items-center justify-between">
					<div>
						<h1
							className="text-h2 text-foreground sm:text-h1"
							style={{ fontFamily: "var(--serif-head)" }}
						>
							Blog
						</h1>
						<p className="mt-2 text-body-md text-muted-foreground">
							Deep dives, cultural roundups, and stories from the
							Zimbabwean slang community.
						</p>
					</div>
					<Link
						to="/blog/rss.xml"
						className="hidden items-center gap-1.5 text-body-xs text-muted-foreground hover:text-foreground sm:flex"
						title="RSS Feed"
					>
						<Icon name="rss" className="size-4" />
						RSS
					</Link>
				</div>
			</div>

			{/* Category filters */}
			<div className="mb-6 flex flex-wrap gap-2">
				<Link
					to="/blog"
					className={`rounded-full px-3 py-1.5 text-body-2xs font-medium transition-colors ${
						!filters.category
							? "bg-primary text-primary-foreground"
							: "bg-muted text-muted-foreground hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-blue-950/30 dark:hover:text-blue-300"
					}`}
				>
					All
				</Link>
				{categories.map(([key, label]) => {
					const params = new URLSearchParams(searchParams);
					params.set("category", key);
					params.delete("page");
					return (
						<Link
							key={key}
							to={`/blog?${params.toString()}`}
							className={`rounded-full px-3 py-1.5 text-body-2xs font-medium transition-colors ${
								filters.category === key
									? "bg-primary text-primary-foreground"
									: "bg-muted text-muted-foreground hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-blue-950/30 dark:hover:text-blue-300"
							}`}
						>
							{label}
						</Link>
					);
				})}
			</div>

			{/* Active tag filter */}
			{filters.tag ? (
				<div className="mb-6 flex items-center gap-2 text-body-xs text-muted-foreground">
					<span>
						Filtered by tag:{" "}
						<strong className="text-foreground">{filters.tag}</strong>
					</span>
					<Link
						to={
							filters.category
								? `/blog?category=${filters.category}`
								: "/blog"
						}
						className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
					>
						Clear
					</Link>
				</div>
			) : null}

			{/* Posts grid */}
			{posts.length === 0 ? (
				<div className="py-16 text-center">
					<Icon
						name="book-open"
						className="mx-auto mb-4 size-12 text-muted-foreground/50"
					/>
					<p className="text-body-lg text-muted-foreground">
						No blog posts yet. Check back soon!
					</p>
				</div>
			) : (
				<>
					<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
						{posts.map((post) => (
							<BlogCard key={post.slug} post={post} />
						))}
					</div>

					{/* Pagination */}
					{pagination.totalPages > 1 ? (
						<div className="mt-8">
							<Pagination
								currentPage={pagination.currentPage}
								totalPages={pagination.totalPages}
								basePath="/blog"
							/>
						</div>
					) : null}
				</>
			)}

			{/* Tags sidebar (shown at bottom on mobile) */}
			{tags.length > 0 ? (
				<div className="mt-12 border-t border-border pt-8">
					<h2 className="mb-4 text-body-sm font-semibold text-foreground">
						Browse by Tag
					</h2>
					<div className="flex flex-wrap gap-2">
						{tags.map((tag) => (
							<Link
								key={tag}
								to={`/blog?tag=${encodeURIComponent(tag)}`}
								className={`rounded-full px-3 py-1 text-body-2xs transition-colors ${
									filters.tag === tag
										? "bg-blue-600 text-white"
										: "bg-muted text-muted-foreground hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-blue-950/30 dark:hover:text-blue-300"
								}`}
							>
								{tag}
							</Link>
						))}
					</div>
				</div>
			) : null}
		</div>
	);
}
